export interface PrintSettings {
	autoPrint: boolean;
	autoPrintScheduled: boolean;
	paperWidth: '58mm' | '80mm';
	customMessage?: string;
	receiptEnabled: boolean;
	showLogo: boolean;
	/**
	 * Mostra o documento na tela antes de imprimir — recibo e relatório em PDF.
	 * Nasce LIGADO (UX-027/UX-028): antes dele não existia caminho nenhum para
	 * ver o recibo antes que ele saísse na impressora, e o PDF do relatório só
	 * podia ser conferido por quem tivesse leitor de PDF na máquina. Desligado,
	 * o recibo imprime direto e o PDF apenas baixa.
	 */
	previewBeforePrint: boolean;
}

export interface EmailSettings {
	sendReportOnClose: boolean;
	birthdayEmailEnabled: boolean;
	birthdayRecipients: string[];
	financialReminderDays: number;
	/** Gate do canal e-mail para o lembrete de pagamento (D-N). Migrado de `financial.sendPaymentReminders`. */
	sendPaymentReminders: boolean;
	/** Gate do canal e-mail para o alerta de atraso. Migrado de `financial.sendOverdueAlerts`. */
	sendOverdueAlerts: boolean;
	receiptEmailEnabled: boolean;
	closingEmailRecipients: string[];
	expenseAlertRecipients: string[];
}

export interface FinancialCronSettings {
	advanceDays: number;
	/**
	 * Permite editar o valor ao dar baixa numa cobrança/mensalidade pendente.
	 * Quando false (padrão), a baixa quita pelo valor exato cobrado.
	 */
	allowEditAmountOnSettle: boolean;
	/**
	 * O que fazer com a diferença quando o valor editado na baixa é menor que o
	 * cobrado (só tem efeito com `allowEditAmountOnSettle` ligado): FORGIVE não
	 * gera cobrança nenhuma pelo saldo (padrão); ASK mostra um checkbox no popup
	 * de baixa para o operador decidir por transação; ALWAYS sempre gera uma
	 * cobrança avulsa pela diferença.
	 */
	residualChargePolicy: 'FORGIVE' | 'ASK' | 'ALWAYS';
	/**
	 * Dias somados a hoje pra pré-preencher o vencimento no formulário de
	 * cobrança avulsa. O campo continua editável (pode virar hoje mesmo ou
	 * qualquer dia futuro) — isto só evita o formulário nascer sem data.
	 */
	defaultDueDays: number;
}

export const DEFAULT_FINANCIAL_CRON_SETTINGS: FinancialCronSettings = {
	advanceDays: 10,
	allowEditAmountOnSettle: false,
	residualChargePolicy: 'FORGIVE',
	defaultDueDays: 5,
};

/**
 * Faixas de antecedência (FEAT-032), espelhando o backend
 * (`temple-settings.interface.ts`). Geração no mínimo 5 dias antes e lembrete no
 * máximo 3 dias antes garantem espaçamento mínimo de 2 dias entre os dois e-mails.
 * Lembrete 0 = no dia do vencimento.
 */
export const ADVANCE_DAYS_MIN = 5;
export const ADVANCE_DAYS_MAX = 25;
export const REMINDER_DAYS_MIN = 0;
export const REMINDER_DAYS_MAX = 3;
/** Vencimento padrão (0-20 dias) — 0 = vence hoje. Eixo diferente de advanceDays. */
export const DEFAULT_DUE_DAYS_MIN = 0;
export const DEFAULT_DUE_DAYS_MAX = 20;

export const clampAdvanceDays = (value: number | undefined | null): number =>
	Math.min(Math.max(value ?? DEFAULT_FINANCIAL_CRON_SETTINGS.advanceDays, ADVANCE_DAYS_MIN), ADVANCE_DAYS_MAX);

export const clampReminderDays = (value: number | undefined | null): number =>
	Math.min(Math.max(value ?? DEFAULT_EMAIL_SETTINGS.financialReminderDays, REMINDER_DAYS_MIN), REMINDER_DAYS_MAX);

export const clampDefaultDueDays = (value: number | undefined | null): number =>
	Math.min(Math.max(value ?? DEFAULT_FINANCIAL_CRON_SETTINGS.defaultDueDays, DEFAULT_DUE_DAYS_MIN), DEFAULT_DUE_DAYS_MAX);

export interface AttendanceTimeSettings {
	warningMinutes: number;
	limitMinutes: number;
}

export interface PreSenhaSettings {
	enabled: boolean;
	currentNumber: number;
}

export const DEFAULT_PRE_SENHA_SETTINGS: PreSenhaSettings = {
	enabled: false,
	currentNumber: 0,
};

export const DEFAULT_ATTENDANCE_TIME_SETTINGS: AttendanceTimeSettings = {
	warningMinutes: 10,
	limitMinutes: 15,
};

export interface WisdomQuoteSettings {
	systemQuoteWeight: number; // 0-100
}

export const DEFAULT_WISDOM_QUOTE_SETTINGS: WisdomQuoteSettings = {
	systemQuoteWeight: 70,
};

export interface PixSettings {
	enabled: boolean;
	pixKeyType: 'CPF' | 'CNPJ' | 'EMAIL' | 'PHONE' | 'RANDOM';
	pixKey: string;
	pixRecipientName: string;
	pixCity: string;
}

/**
 * Alertas via WhatsApp (FEAT-026). O envio real depende também da
 * plataforma estar habilitada e, para membros, de telefone válido +
 * opt-in da pessoa.
 */
export interface WhatsappSettings {
	enabled: boolean;
	sendPaymentReminders: boolean;
	sendOverdueAlerts: boolean;
	sendPaymentConfirmations: boolean;
	/** Alertas para admins (pagamento recebido, despesas, duplicidade, fechamento, débito falhou) */
	sendAdminAlerts: boolean;
}

export const DEFAULT_WHATSAPP_SETTINGS: WhatsappSettings = {
	enabled: false,
	sendPaymentReminders: true,
	sendOverdueAlerts: true,
	sendPaymentConfirmations: true,
	sendAdminAlerts: true,
};

/**
 * Agenda pública / reservas (FEAT-029). O liga/desliga geral fica na coluna
 * `Temple.publicAgendaEnabled` (não aqui). `contactChannel` é só 'EMAIL' nesta
 * fase (WhatsApp depois). Aprovação de reserva é sempre manual.
 */
export interface PublicAgendaSettings {
	acceptReservations: boolean;
	requirePhone: boolean;
	requireEmail: boolean;
	/** Exige foto (tirada pela webcam) para concluir a reserva. */
	requirePhoto: boolean;
	contactChannel: 'EMAIL' | 'WHATSAPP';
	/** Descrição curta — topo da página, abaixo do nome. */
	welcomeText: string;
	/** Descrição longa — aba "Mais informações". */
	longDescription: string;
	/** Dados do cadastro do templo publicados na página (desligados por padrão). */
	showPhone: boolean;
	showWhatsapp: boolean;
	showEmail: boolean;
	showAddress: boolean;
	showInstagram: boolean;
	showFacebook: boolean;
	/** Quantos dias à frente a agenda mostra rituais. */
	agendaWindowDays: number;
	/** Notificar admins por e-mail a cada reserva confirmada aguardando análise. */
	notifyOnReservation: boolean;
}

export const DEFAULT_PUBLIC_AGENDA_SETTINGS: PublicAgendaSettings = {
	acceptReservations: false,
	requirePhone: true,
	requireEmail: true,
	requirePhoto: false,
	contactChannel: 'EMAIL',
	welcomeText: '',
	longDescription: '',
	showPhone: false,
	showWhatsapp: false,
	showEmail: false,
	showAddress: false,
	showInstagram: false,
	showFacebook: false,
	agendaWindowDays: 60,
	notifyOnReservation: true,
};

/**
 * Lançamento automático da taxa do Mercado Pago como despesa (BUG-015).
 * Só se aplica a templos com a integração conectada. Ativo por padrão.
 */
export interface GatewayFeeSettings {
	enabled: boolean;
	fundMode: 'SAME' | 'FIXED';
	fundId: string | null;
}

export const DEFAULT_GATEWAY_FEE_SETTINGS: GatewayFeeSettings = {
	enabled: true,
	fundMode: 'SAME',
	fundId: null,
};

/**
 * Cobrança automática (débito recorrente via Mercado Pago). Liga/desliga a oferta
 * de novo débito automático (botão na página do membro e no admin). Desligado,
 * fica só o PIX. Ligado por padrão quando ausente.
 */
export interface AutoDebitSettings {
	enabled: boolean;
}

export const DEFAULT_AUTO_DEBIT_SETTINGS: AutoDebitSettings = {
	enabled: true,
};

export interface TempleSettings {
	print?: PrintSettings;
	email?: EmailSettings;
	financial?: FinancialCronSettings;
	attendanceTime?: AttendanceTimeSettings;
	preSenha?: PreSenhaSettings;
	wisdomQuotes?: WisdomQuoteSettings;
	pix?: PixSettings;
	whatsapp?: WhatsappSettings;
	publicAgenda?: PublicAgendaSettings;
	gatewayFee?: GatewayFeeSettings;
	autoDebit?: AutoDebitSettings;
}

export const DEFAULT_PRINT_SETTINGS: PrintSettings = {
	autoPrint: false,
	autoPrintScheduled: false,
	paperWidth: '80mm',
	customMessage: '',
	receiptEnabled: false,
	showLogo: true,
	previewBeforePrint: true,
};

export const DEFAULT_EMAIL_SETTINGS: EmailSettings = {
	sendReportOnClose: true,
	birthdayEmailEnabled: false,
	birthdayRecipients: [],
	financialReminderDays: 3,
	sendPaymentReminders: true,
	sendOverdueAlerts: true,
	receiptEmailEnabled: false,
	closingEmailRecipients: [],
	expenseAlertRecipients: ['admins'],
};
